import React from "react"
import { NavLink, useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "hooks/useAuth"
import { isNotEmpty } from "utils/validation"
import FormElement from "components/Form/FormElement"
import browserStorageService, { REMEMBER_MAIL_KEY } from "services/browserStorage"
import { useTranslation } from "react-i18next"
import { publicRoutes } from "router/routes"

const defaultFormFields = {
    email: "",
    password: "",
    rememberMail: false
}

const defaultFormEvents = {
    ready: true
}

export const FormSignin = ({ currentSubdomain }) => {
    const [formFields, setFormFields] = React.useState(defaultFormFields)
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

    const [errors, setErrors] = React.useState([])
    const [showLocalErrors, setShowLocalErrors] = React.useState(false)
    const [showGlobalError, setShowGlobalError] = React.useState(false)

    const { login } = useAuth()
    const { t } = useTranslation()
    const { state: routeState } = useLocation()
	const navigate = useNavigate()

    React.useEffect(() => {
        // We set email when the user activate his account
        if (routeState?.user_info?.mail) {
            setFormFields(s => ({
                ...s,
                email: routeState.user_info.mail
            }))
        }
    }, [routeState]) // eslint-disable-line

    React.useEffect(() => {
        const email = browserStorageService.get(REMEMBER_MAIL_KEY)
        if (isNotEmpty(email)) {
            setFormFields({
                ...formFields,
                email,
                rememberMail: true
            })
        }
    }, []) // eslint-disable-line

    //const goToCreateAccount = () => navigate(publicRoutes.signup.path)

    const handleInputChange = (event) => {
        const target = event.target
        const value = target.type === "checkbox" ? target.checked : target.value
        const name = target.name

        setFormFields({
            ...formFields,
            [name]: value
        })
    }

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            // hide error
            setShowLocalErrors(false)
            // prevent form to be submitted again
            setFormEvents({
                ...formEvents,
                ready: false
            })

            if (formFields.rememberMail) {
                browserStorageService.set(REMEMBER_MAIL_KEY, formFields.email.trim())
            } else {
                browserStorageService.remove(REMEMBER_MAIL_KEY)
            }
            const signIn = await login(formFields.email.trim(), formFields.password.trim())

            if (!signIn) {
                setShowGlobalError(true)
                setFormEvents({
                    ...formEvents,
                    ready: true
                })
            }
            else {
                // Redirect to logged view
                navigate(publicRoutes.login.path)
            }
        }
    }

    return (
        <div className="l-form-width mx-auto">
            <form id="formSignIn" autoComplete="off">
                {showGlobalError &&
                    <p className="text-xs text-danger mb-5">{t("signin_global_error", { ns: "api" })}</p>
                }
                {/* Mail */}
                <FormElement
                    value={formFields.email.trim()}
                    id="email"
                    name="email"
                    type="email"
                    label={t("mail", { ns: "auth" })}
                    className="mb-7"
                    required={true}
                    showErrors={showLocalErrors}
                    onValidate={handleValidation}
                    onChange={handleInputChange}
                    autocomplete="off"
                />

                {/* Password */}
                <FormElement
                    value={formFields.password}
                    id="password"
                    name="password"
                    type="password"
                    label={t("password", { ns: "auth" })}
                    className="mb-2"
                    required={true}
                    showErrors={showLocalErrors}
                    onValidate={handleValidation}
                    onChange={handleInputChange}
                    autocomplete="new-password"
                />

                <div className="flex justify-end items-center flex-col md:flex-row">
                    <NavLink to="/mot-de-passe-perdu" className="text-xs underline mb-2">
                        {t("forgot_password.link", { ns: "auth" })}
                    </NavLink>
                </div>

                {/* Remember id */}
                <FormElement
                    value={formFields.rememberMail}
                    name="rememberMail"
                    type="checkbox"
                    label=""
                    checkboxLabel={t("remember_mail", { ns: "auth" })}
                    className="py-4"
                    showErrors={showLocalErrors}
                    onValidate={handleValidation}
                    onChange={handleInputChange}
                />

                <div className="flex justify-center mt-7 mb-7">
                    {formEvents.ready &&
                        <button
                            type="submit"
                            className="c-btn c-btn--primary px-12"
                            onClick={handleSubmit}
                        >
                            {t("submit", { ns: "auth" })}
                        </button>
                    }
                    {!formEvents.ready &&
                        <div className="c-spinner"></div>
                    }
                </div>


                {currentSubdomain?.name === "Bicycode" &&
                    <>
                        <div className="divider"><span>{t("global.or")}</span></div>

                        <div className="flex justify-center mt-7 mb-5">
                            {/*<button
                                type="submit"
                                className="c-btn c-btn--primary px-12"
                                onClick={goToCreateAccount}
                            >
                                {t("create_account.title", { ns: "user" })}
                            </button>*/}
                        </div>
                        <a href="https://signin.bicycode.eu/auth/register" target="_blank" rel="noreferrer" className="c-btn c-btn--primary px-12">
                            {t("create_account.title", { ns: "user" })}
                        </a>
                    </>
                }
            </form>
        </div>
    )
}
